import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  deleteScreenshotSagaAction,
  getScreenshotsSagaAction,
  upsertScreenshotsSagaAction,
} from "../../../../../../redux/saga/projects/saga-actions";
import { useTimelineStepPermissions } from "../../../../../../hooks/useTimelineStepPermissions";
import ProjectDetailsEditIcon from "./ProjectDetailsEditIcon";
import AddScreenshotsModal, {
  AddScreenshotsSavePayload,
} from "./screenshots/AddScreenshotsModal";
import ScreenshotMedia from "./screenshots/ScreenshotMedia";
import ScreenshotPreviewModal from "./screenshots/ScreenshotPreviewModal";
import {
  DEFAULT_SCREENSHOT_MAX_FILES,
  DEFAULT_SCREENSHOT_TEMPLATE,
  SCREENSHOT_TEMPLATE_SLOTS,
  ScreenshotItem,
  toScreenshotItem,
} from "./screenshots/screenshotsConfig";
import "./ScreenshotsSection.scss";

type ScreenshotsSectionProps = {
  integrationTypeId: number;
};

const ScreenshotsSection: React.FC<ScreenshotsSectionProps> = ({
  integrationTypeId,
}) => {
  const dispatch = useAppDispatch();
  const { canEditTimeline } = useTimelineStepPermissions();
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { screenshots } = useAppSelector((state) => state.projects);
  const { screenshotsLoading, screenshotsSaving } = useAppSelector(
    (state) => state.projects.status
  );
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!Number.isFinite(integrationTypeId) || integrationTypeId <= 0) return;
    dispatch(getScreenshotsSagaAction(integrationTypeId));
  }, [dispatch, integrationTypeId]);

  const serverData = React.useMemo(() => {
    if (!screenshots || screenshots.integrationTypeId !== integrationTypeId) {
      return null;
    }
    const selected = screenshots.selected.map(toScreenshotItem);
    return {
      template: screenshots.template,
      selected,
      uploaded: [...selected, ...screenshots.available.map(toScreenshotItem)],
      maxFiles: screenshots.maxCount,
    };
  }, [screenshots, integrationTypeId]);

  const selectedScreenshots = serverData?.selected ?? [];
  const uploadedScreenshots = serverData?.uploaded ?? [];
  const template = serverData?.template ?? DEFAULT_SCREENSHOT_TEMPLATE;
  const modalInitialTemplate =
    serverData && serverData.uploaded.length > 0 ? serverData.template : null;
  const maxFiles = serverData?.maxFiles ?? DEFAULT_SCREENSHOT_MAX_FILES;

  const visibleScreenshots = selectedScreenshots.slice(
    0,
    SCREENSHOT_TEMPLATE_SLOTS[template] ?? selectedScreenshots.length
  );
  const isLoading = screenshotsLoading && !serverData;

  const getSlotVariant = (index: number) => {
    if (template === "three_mobile") return "mobile";
    if (template === "two_desktops") return "desktop";
    return index === 0 ? "mobile" : "desktop";
  };

  const openEditModal = () => {
    setSaveError(null);
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (screenshotsSaving) return;
    setSaveError(null);
    setIsEditOpen(false);
  };

  const handleSave = (payload: AddScreenshotsSavePayload) => {
    const uploadedBy = loggedUserDetail?.ID;
    if (!uploadedBy) {
      setSaveError("Unable to identify the logged-in user. Please sign in again.");
      return;
    }

    setSaveError(null);
    dispatch(
      upsertScreenshotsSagaAction(
        {
          integrationTypeId,
          uploadedBy,
          template: payload.template,
          selectedMediaIds: payload.selectedMediaIds,
          selectedIndexes: payload.selectedIndexes,
          files: payload.files,
        },
        () => setIsEditOpen(false),
        (message) => setSaveError(message)
      )
    );
  };

  const handleDeleteScreenshot = (
    item: ScreenshotItem,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    const mediaId = Number(item.id);
    if (!Number.isFinite(mediaId)) {
      onError("Unable to identify this screenshot.");
      return;
    }
    dispatch(
      deleteScreenshotSagaAction(mediaId, integrationTypeId, onSuccess, onError)
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return <p className="screenshots-section__empty">Loading screenshots...</p>;
    }

    if (visibleScreenshots.length === 0) {
      return (
        <p className="screenshots-section__empty">No screenshots available.</p>
      );
    }

    return (
      <div
        className={`screenshots-section__gallery screenshots-section__gallery--${template}`}
      >
        {visibleScreenshots.map((item, index) => (
          <figure
            key={item.id}
            className={`screenshots-section__shot screenshots-section__shot--${getSlotVariant(
              index
            )}`}
            role="button"
            tabIndex={0}
            aria-label={`Preview ${item.name}`}
            onClick={() => setPreviewIndex(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setPreviewIndex(index);
              }
            }}
          >
            <ScreenshotMedia
              url={item.url}
              name={item.name}
              mimeType={item.mimeType}
            />
          </figure>
        ))}
      </div>
    );
  };

  return (
    <>
      <div
        className={`screenshots-section${
          !canEditTimeline ? " screenshots-section--disabled" : ""
        }`}
      >
        <div className="screenshots-section__header">
          <h3 className="screenshots-section__title">Screenshots</h3>
          {canEditTimeline ? (
            <button
              type="button"
              className="screenshots-section__edit"
              onClick={openEditModal}
              disabled={isLoading}
            >
              <ProjectDetailsEditIcon className="screenshots-section__edit-icon" />
              {uploadedScreenshots.length > 0 ? "Edit" : "Add"}
            </button>
          ) : null}
        </div>

        <div
          className={`screenshots-section__body${
            !isLoading && visibleScreenshots.length > 0
              ? " screenshots-section__body--gallery"
              : ""
          }`}
        >
          {renderBody()}
        </div>
      </div>

      {isEditOpen ? (
        <AddScreenshotsModal
          initialTemplate={modalInitialTemplate}
          initialScreenshots={uploadedScreenshots}
          initialSelectedIds={selectedScreenshots.map((item) => item.id)}
          maxFiles={maxFiles}
          isSaving={screenshotsSaving}
          errorMessage={saveError}
          onClose={closeEditModal}
          onSave={handleSave}
          onDeleteScreenshot={handleDeleteScreenshot}
        />
      ) : null}

      {previewIndex !== null && visibleScreenshots[previewIndex] ? (
        <ScreenshotPreviewModal
          screenshots={visibleScreenshots}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      ) : null}
    </>
  );
};

export default ScreenshotsSection;
